'use client';

import { type GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';
import { useLayoutEffect, useState } from 'react';
import { getAccessToken, getAuth0LogoutGeneration, getAuthSessionEpoch, isAccessTokenAuth0, isAuth0LogoutInFlight, isLastActiveExpired, registerAuth0LogoutHandler, registerAuth0SessionRevalidator, registerAuth0TokenRefresher, removeRegularAuthTokens, revalidateAuth0Session, SESSION_EVALUATED_DOM_EVENT, setAuth0AccessToken, unregisterAuth0LogoutHandler, unregisterAuth0SessionRevalidator, unregisterAuth0TokenRefresher, watchAuth0LogoutAcrossTabs } from '@libs/core';
import { useAuth0Logout } from '../../hooks/useAuth0Logout';
import { useAuth0SessionLogout } from '../../hooks/useAuth0SessionLogout';
import { logSentryError, markAuth0AutoshipEnrollComplete, markAuth0LoginCompleted, readAuth0ReturnTo, waitForAuth0CartMerge } from '../../utils';
import { auth0TokenSyncSentryTags } from './auth0TokenSyncSentryTags';
import { clearSdkSessionAfterInterruptedLogout } from './clear-sdk-session-after-interrupted-logout';
import { completeAuth0CallbackRedirect, isAuth0CallbackPath, setPendingAuth0ReturnTo } from './pendingAuth0ReturnTo';
const ACCESS_TOKEN_SYNC_SENTRY = {
  component: 'useAuth0AccessTokenSync',
  action: 'getAccessTokenSilently'
} as const;

// Safari and cross-site hosts may report login_required or missing_refresh_token
// while the access cookie is still usable. Only explicit revocation errors end
// the local session.
const REVOKED_SESSION_ERROR_CODES = new Set(['invalid_grant', 'invalid_refresh_token']);
type GetAccessTokenSilently = (options?: GetTokenSilentlyOptions) => Promise<string>;
type AccessTokenSyncContext = {
  isCurrentEffect: () => boolean;
  getAccessTokenSilently: GetAccessTokenSilently;
  clearAuth0SdkSession: () => Promise<void>;
  setSyncing: (value: boolean) => void;
  onCallbackError: (error: Error) => void;
  auth0Logout: ReturnType<typeof useAuth0Logout>;
  auth0SessionLogout: ReturnType<typeof useAuth0SessionLogout>;
};
type HandleAccessTokenSyncErrorInput = {
  ctx: AccessTokenSyncContext;
  error: unknown;
  isCallback?: boolean;
};
function dispatchSessionEvaluated() {
  globalThis.window.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));
}
function logAccessTokenSyncError(error: unknown) {
  logSentryError(error, auth0TokenSyncSentryTags(ACCESS_TOKEN_SYNC_SENTRY));
}
function isAuth0SessionRevokedError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('error' in error)) {
    return false;
  }
  return typeof error.error === 'string' && REVOKED_SESSION_ERROR_CODES.has(error.error);
}
async function handleAccessTokenSyncError({
  ctx,
  error,
  isCallback = false
}: HandleAccessTokenSyncErrorInput): Promise<boolean> {
  const syncError = error instanceof Error ? error : new Error('Failed to sync the Auth0 access token');
  logAccessTokenSyncError(syncError);
  if (!ctx.isCurrentEffect()) {
    return false;
  }
  if (isCallback) {
    ctx.onCallbackError(syncError);
    return false;
  }
  if (!isAuth0SessionRevokedError(error)) {
    return false;
  }
  await ctx.auth0Logout();
  return true;
}
type ShouldPersistAccessTokenInput = {
  ctx: AccessTokenSyncContext;
  accessToken: string;
  epochAtStart: number;
  logoutGenerationAtStart: number;
};
function shouldPersistAccessToken({
  ctx,
  accessToken,
  epochAtStart,
  logoutGenerationAtStart
}: ShouldPersistAccessTokenInput): boolean {
  return Boolean(ctx.isCurrentEffect() && accessToken && getAuthSessionEpoch() === epochAtStart && getAuth0LogoutGeneration() === logoutGenerationAtStart && !isLastActiveExpired() && !isAuth0LogoutInFlight());
}
async function resyncExistingAccessToken(ctx: AccessTokenSyncContext) {
  if (isAuth0LogoutInFlight()) {
    return;
  }
  if (isLastActiveExpired()) {
    await ctx.auth0SessionLogout({});
    return;
  }
  ctx.setSyncing(true);
  const epochAtStart = getAuthSessionEpoch();
  const logoutGenerationAtStart = getAuth0LogoutGeneration();
  try {
    const accessToken = await ctx.getAccessTokenSilently({
      cacheMode: 'off'
    });
    const logoutInterrupted = await clearSdkSessionAfterInterruptedLogout({
      logoutGenerationAtStart,
      clearAuth0SdkSession: ctx.clearAuth0SdkSession,
      reportError: logAccessTokenSyncError
    });
    if (!logoutInterrupted && shouldPersistAccessToken({
      ctx,
      accessToken,
      epochAtStart,
      logoutGenerationAtStart
    })) {
      setAuth0AccessToken(accessToken);
    }
  } catch (error) {
    if (await handleAccessTokenSyncError({
      ctx,
      error
    })) {
      return;
    }
  } finally {
    ctx.setSyncing(false);
  }
  if (ctx.isCurrentEffect()) {
    dispatchSessionEvaluated();
  }
}
async function syncUnauthenticatedAccessToken(ctx: AccessTokenSyncContext) {
  if (!isAccessTokenAuth0()) {
    return;
  }
  const existingAccessToken = getAccessToken();
  if (existingAccessToken) {
    // Safari can finish SDK init with `isAuthenticated: false` on cross-site
    // hosts (ephemeral, localhost) before silent auth runs, or when iframe-based
    // renewal is blocked. Keep the legacy cookie and try to re-sync instead of
    // clearing cookies the rest of the app still relies on — unless inactivity
    // already ended the local session, in which case a silent write would
    // reopen cookie-gated routes.
    await resyncExistingAccessToken(ctx);
    return;
  }
  removeRegularAuthTokens();
  dispatchSessionEvaluated();
}
async function syncAuthenticatedAccessToken(ctx: AccessTokenSyncContext) {
  if (isAuth0LogoutInFlight()) {
    return;
  }
  if (isLastActiveExpired()) {
    await ctx.auth0SessionLogout({});
    return;
  }
  ctx.setSyncing(true);
  const epochAtStart = getAuthSessionEpoch();
  const logoutGenerationAtStart = getAuth0LogoutGeneration();
  let accessToken = '';
  const isCallback = isAuth0CallbackPath(globalThis.window.location.pathname);
  try {
    accessToken = isCallback ? await ctx.getAccessTokenSilently() : await ctx.getAccessTokenSilently({
      cacheMode: 'off'
    });
  } catch (error) {
    await handleAccessTokenSyncError({
      ctx,
      error,
      isCallback
    });
    return;
  } finally {
    ctx.setSyncing(false);
  }
  if (await clearSdkSessionAfterInterruptedLogout({
    logoutGenerationAtStart,
    clearAuth0SdkSession: ctx.clearAuth0SdkSession,
    reportError: logAccessTokenSyncError
  })) {
    dispatchSessionEvaluated();
    return;
  }
  if (!ctx.isCurrentEffect()) {
    return;
  }
  if (shouldPersistAccessToken({
    ctx,
    accessToken,
    epochAtStart,
    logoutGenerationAtStart
  })) {
    setAuth0AccessToken(accessToken);
    dispatchSessionEvaluated();
    if (isCallback) {
      await redirectAfterCartMerge(ctx);
      return;
    }
    completeAuth0CallbackRedirect();
    return;
  }

  // Wake session waiters even when silent token sync fails or skips persist
  dispatchSessionEvaluated();
}
async function redirectAfterCartMerge(ctx: AccessTokenSyncContext) {
  await waitForAuth0CartMerge();
  if (!ctx.isCurrentEffect()) {
    return;
  }
  const postMergeReturnTo = readAuth0ReturnTo();
  if (postMergeReturnTo) {
    setPendingAuth0ReturnTo(postMergeReturnTo);
  }

  // Complete only analytics signals validated for this callback transaction.
  // Other Auth0 returns (change-email) must not inherit stale login intent.
  markAuth0LoginCompleted();
  markAuth0AutoshipEnrollComplete();
  completeAuth0CallbackRedirect();
}

/**
 * Keeps the legacy `accessToken` cookie in sync with the Auth0 SDK session.
 *
 * Performs an initial sync on auth state changes so the cookie exists before the
 * first API call.
 *
 * Registers demand-driven Auth0 bridges used by core fetch/session layers:
 * token refresh on 401, remote-session revalidation, and session termination
 * on inactivity expiry.
 * Logs out on mount when `lastActive` is already past the inactivity threshold
 * so SSR pages without an immediate client fetch still terminate stale sessions.
 * That teardown runs for both `isAuthenticated` and the Safari path where the SDK
 * reports unauthenticated while a legacy Auth0 `accessToken` cookie is still present.
 *
 * After a Universal Login callback, waits for guest-cart merge, then document-
 * navigates to the pending `returnTo` (preferring a post-merge path) only once
 * the cookie is written so Next.js cookie redirects can run. Token sync failures
 * stay on the callback route and surface through the existing error boundary.
 */
export function useAuth0AccessTokenSync() {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    logout
  } = useAuth0();
  const auth0Logout = useAuth0Logout();
  const auth0SessionLogout = useAuth0SessionLogout();
  const [callbackError, setCallbackError] = useState<Error>();
  useLayoutEffect(() => {
    // Subscribe before the `isLoading` return. A storage event during SDK init
    // is otherwise missed, and this tab can write the access cookie back.
    watchAuth0LogoutAcrossTabs();

    // Guards against a stale async token fetch writing cookies after auth state changes.
    let isCurrentEffect = true;
    // Serializes initial sync runs so overlapping auth transitions cannot issue
    // redundant token fetches or out-of-order `setAccessToken` writes.
    let isSyncing = false;
    const ctx: AccessTokenSyncContext = {
      isCurrentEffect: () => isCurrentEffect,
      getAccessTokenSilently: options => options === undefined ? getAccessTokenSilently() : getAccessTokenSilently(options),
      clearAuth0SdkSession: () => logout({
        openUrl: false
      }),
      setSyncing: value => {
        isSyncing = value;
      },
      onCallbackError: setCallbackError,
      auth0Logout,
      auth0SessionLogout
    };
    registerAuth0TokenRefresher(async () => {
      const logoutGenerationAtStart = getAuth0LogoutGeneration();
      try {
        // After a 401 the SDK may still consider the access token valid; bypass cache
        // so /oauth/token runs and the refresh token can issue a new JWT.
        const accessToken = await getAccessTokenSilently({
          cacheMode: 'off'
        });
        const logoutInterrupted = await clearSdkSessionAfterInterruptedLogout({
          logoutGenerationAtStart,
          clearAuth0SdkSession: ctx.clearAuth0SdkSession,
          reportError: logAccessTokenSyncError
        });
        return logoutInterrupted ? null : accessToken;
      } catch (error) {
        await handleAccessTokenSyncError({
          ctx,
          error
        });
        return null;
      }
    });
    registerAuth0LogoutHandler(auth0SessionLogout);
    registerAuth0SessionRevalidator(revalidateAuth0Session);
    async function syncAccessToken() {
      if (isLoading || isSyncing) {
        return;
      }
      if (isAuth0LogoutInFlight()) {
        // The provider's own initialization can cache tokens after logout.
        // Wait for loading to finish, then clear that late SDK session too.
        await clearSdkSessionAfterInterruptedLogout({
          logoutGenerationAtStart: getAuth0LogoutGeneration(),
          clearAuth0SdkSession: ctx.clearAuth0SdkSession,
          reportError: logAccessTokenSyncError
        });
        dispatchSessionEvaluated();
        return;
      }
      if (!isAuthenticated) {
        await syncUnauthenticatedAccessToken(ctx);
        return;
      }
      await syncAuthenticatedAccessToken(ctx);
    }
    void syncAccessToken();
    return () => {
      isCurrentEffect = false;
      unregisterAuth0TokenRefresher();
      unregisterAuth0LogoutHandler();
      unregisterAuth0SessionRevalidator();
    };
  }, [auth0Logout, auth0SessionLogout, getAccessTokenSilently, isAuthenticated, isLoading, logout]);
  if (callbackError) {
    throw callbackError;
  }
}